import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

export default function AboutPage() {
  const values = [
    {
      icon: "ri-eye-line",
      title: "Clarity over Complexity",
      desc: "Every homeowner deserves a clear, actionable plan — not vague inspiration boards or overwhelming options.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Budget-Conscious Planning",
      desc: "Beautiful design shouldn't break the bank. Every recommendation is aligned to your budget for maximum value.",
    },
    {
      icon: "ri-scales-3-line",
      title: "Practical + Aesthetic Balance",
      desc: "We bridge beautiful and liveable — spaces that look stunning and work perfectly for real life.",
    },
    {
      icon: "ri-user-heart-line",
      title: "Client-First Thinking",
      desc: "Your goals, lifestyle, and preferences drive every decision. We listen first, then design around what matters most.",
    },
  ];

  const stats = [
    { number: "500+", label: "Design Proposals Delivered" },
    { number: "98%", label: "Client Satisfaction Rate" },
    { number: "12+", label: "Years of Design Experience" },
    { number: "$2M+", label: "In Client Savings Estimated" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main>
        <section className="pt-36 pb-24 relative min-h-[540px] flex items-center">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/a71f03b088ae3cac81f6fa3ecf970b4a.jpg"
              alt="The Design Proposal studio"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/72 via-black/45 to-black/15"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-xl">
              <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-4">
                Our Story
              </span>
              <h1
                className="text-5xl lg:text-6xl font-semibold text-white leading-tight mb-5"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                About The Design Proposal
              </h1>
              <p className="text-white/70 leading-relaxed text-lg">
                Founded with one mission: make professional interior design accessible, actionable, and genuinely helpful for every homeowner.
              </p>
            </div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                  Our Origin
                </span>
                <h2
                  className="text-4xl font-semibold text-[#111111] mb-6 leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Designed to Simplify the Design Process
                </h2>
                <p className="text-[#5C5C5C] leading-relaxed mb-5">
                  The Design Proposal was born out of frustration. Too many homeowners were starting renovations or redesigns without a clear plan — making expensive decisions based on guesswork, trend chasing, or conflicting advice from the internet.
                </p>
                <p className="text-[#5C5C5C] leading-relaxed mb-5">
                  We saw the same pattern repeatedly: homeowners overwhelmed by choices, uncertain about materials, misaligned on budget, and ultimately making costly mistakes that could have been avoided with proper planning.
                </p>
                <p className="text-[#5C5C5C] leading-relaxed">
                  So we built a system. A structured, proposal-based approach to interior design that delivers exactly what homeowners need: a clear, actionable plan that removes the guesswork and makes confident decision-making possible.
                </p>
              </div>
              <div className="relative">
                <div className="rounded-xl overflow-hidden" style={{ height: "500px" }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=warm%20modern%20finished%20luxury%20living%20room%20interior%20design%20result%20beautifully%20staged%20neutral%20warm%20tones%20cozy%20sophisticated%20natural%20light%20elegant%20furniture%20linen%20white%20cream%20editorial%20magazine%20residential%20photography&width=700&height=500&seq=about-story-elite-2026&orientation=portrait"
                    alt="Beautiful finished interior design result"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-5 -right-5 bg-[#C9A961] text-white p-5 rounded-xl">
                  <div
                    className="text-3xl font-bold mb-0.5"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    12+
                  </div>
                  <p className="text-sm text-white/80 whitespace-nowrap">Years of design expertise</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-[#111111]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div
                    className="text-4xl font-semibold text-[#C9A961] mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {stat.number}
                  </div>
                  <p className="text-white/55 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                What We Stand For
              </span>
              <h2
                className="text-4xl font-semibold text-[#111111]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Our Approach to Design
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {values.map((v) => (
                <div key={v.title} className="bg-white rounded-xl p-7 border border-[#EBEBEB]">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C9A961]/10 rounded-xl mb-5">
                    <i className={`${v.icon} text-[#C9A961] text-xl`}></i>
                  </div>
                  <h3
                    className="text-lg font-semibold text-[#111111] mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {v.title}
                  </h3>
                  <p className="text-sm text-[#5C5C5C] leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-[#FAFAFA] rounded-2xl p-10 lg:p-14 border border-[#EBEBEB]">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className="lg:col-span-4">
                  <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                    About The Design Proposal
                  </span>
                  <h2
                    className="text-3xl font-semibold text-[#111111] leading-tight mb-4"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    What We Do & Who We Serve
                  </h2>
                  <Link
                    to="/services"
                    className="whitespace-nowrap inline-flex items-center gap-2 text-sm font-medium text-[#111111] hover:text-[#C9A961] transition-colors cursor-pointer"
                  >
                    View all services
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </Link>
                </div>
                <div className="lg:col-span-8 space-y-4">
                  <p className="text-[#5C5C5C] leading-relaxed text-[15px]">
                    The Design Proposal is a professional interior design planning service founded to help homeowners across the United States navigate renovations and redesigns with confidence. We specialize in structured design proposals that remove guesswork, align with real budgets, and deliver actionable plans — serving clients virtually across all 50 states and locally in select areas.
                  </p>
                  <p className="text-[#5C5C5C] leading-relaxed text-[15px]">
                    Our services include interior design proposals, renovation planning, material and finish selection, room-by-room packages, and virtual consultations. Each service is delivered in a clear, structured format — designed to be handed directly to contractors or executed independently by homeowners.
                  </p>
                  <p className="text-[#5C5C5C] leading-relaxed text-[15px]">
                    We work with homeowners planning renovations, redesigns, new builds, and room refreshes — from single-room projects to complete whole-home transformations. Our approach is client-first, budget-conscious, and focused entirely on practical, beautiful outcomes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="rounded-xl overflow-hidden" style={{ height: "460px" }}>
                <img
                  src="https://readdy.ai/api/search-image?query=interior%20designer%20consulting%20with%20homeowner%20reviewing%20design%20proposal%20document%20warm%20bright%20office%20neutral%20tones%20professional%20friendly%20consultation%20natural%20light%20elegant%20premium%20residential%20design%20service&width=700&height=460&seq=about-expertise-elite-2026&orientation=landscape"
                  alt="Interior design consultation session"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <span className="inline-block text-[#C9A961] text-xs font-semibold uppercase tracking-widest mb-3">
                  Our Background
                </span>
                <h2
                  className="text-4xl font-semibold text-[#111111] mb-6 leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Design Expertise You Can Trust
                </h2>
                <p className="text-[#5C5C5C] leading-relaxed mb-5">
                  Our team brings over a decade of hands-on experience in residential interior design, renovation planning, and space optimization — from single-room refreshes to complete whole-home transformations.
                </p>
                <p className="text-[#5C5C5C] leading-relaxed mb-8">
                  We&apos;ve helped hundreds of homeowners avoid costly mistakes, make confident decisions, and create spaces they genuinely love — through a process that&apos;s structured, transparent, and always aligned to your budget.
                </p>
                <Link
                  to="/#proposal"
                  className="whitespace-nowrap inline-flex items-center gap-2 bg-[#C9A961] text-white font-medium px-7 py-3.5 rounded-md hover:bg-[#b8924d] transition-colors duration-300 cursor-pointer"
                >
                  Start Your Project
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
